import React, { useState, useEffect, useCallback, useMemo } from 'react';
import pluralize from 'pluralize';
import { isEqual } from 'lodash';

// hooks
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLeadsInfiniteQuery } from 'api/leads';

// material-ui
import { Box, Grid } from '@material-ui/core';
import { NewReleases } from '@material-ui/icons';

// components
import Banner from 'components/Banner';
import LeadListActions from 'routes/LeadList/LeadListView/LeadListActions';
import LeadListCardView from 'routes/LeadList/LeadListView/LeadListCardView';
import LeadListTableView from 'routes/LeadList/LeadListView/LeadListTableView';

// selectors
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
import getNextPageFromState from 'store/selectors/getNextPageFromState';
import getLoadingFromState from 'store/selectors/getLoadingFromState';
import getCurrentGroup from 'store/selectors/getCurrentGroup';
import getSliceState from 'store/selectors/getSliceState';
import getPermission from 'store/selectors/getPermission';
import getReportingPlatform from 'store/selectors/getReportingPlatform';
import getCurrentSubscription from 'store/selectors/getCurrentSubscription';
import getIsCurrentGroupChapter from 'store/selectors/getIsCurrentGroupChapter';

// action creators
import { fetchTagsAction } from 'store/actions/tagActions';
import { fetchStatusesAction } from 'store/actions/statusActions';

// helpers
import { LeadsQueryData } from 'routes/LeadList/helpers/interfaces';
import getPushLead from 'store/selectors/getPushLead';
interface LeadListViewProps {
  isCouncilLeadPage?: boolean | null;
}

export default function LeadListView({ isCouncilLeadPage }: LeadListViewProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch: refetchLeads,
  } = useLeadsInfiniteQuery();
  const leads: Lead[] = useMemo(() => (data?.pages ? data?.pages.flat() : []), [
    data,
  ]);
  const totalLeadsCount = useMemo(
    () =>
      data?.pageParams
        ? (data?.pageParams as { total: number }[])[data?.pageParams.length - 1]
            ?.total
        : 0,
    [data]
  );

  const {
    currentGroupId,
    currentGroup,
    currentSubscription,
    isCurrentGroupChapter,
    nextPage,
    tags = [],
    tagsLoading,
    pushLead,
    reportingPlatform,
  } = useSelector(
    state => ({
      currentGroupId: getCurrentGroupId(state),
      currentGroup: getCurrentGroup(state),
      currentSubscription: getCurrentSubscription(state),
      isCurrentGroupChapter: getIsCurrentGroupChapter(state),
      nextPage: getNextPageFromState('lead')(state),
      tagsLoading: getLoadingFromState('tag', null, false)(state),
      tags: (getSliceState('tag')(state) || {}).data,
      pushLead: getPushLead(state),
      reportingPlatform: getReportingPlatform(state),
    }),
    isEqual
  );

  const isFreeSubscription =
    isCurrentGroupChapter &&
    currentSubscription &&
    currentSubscription.getIn(['plan', 'name']).includes('Free');

  const loading = useMemo(
    () =>
      isLoading || isFetchingNextPage || !!(isCouncilLeadPage && tagsLoading),
    [isLoading, isFetchingNextPage, isCouncilLeadPage, tagsLoading]
  );
  const reportingPlatformDisplayName = reportingPlatform?.displayName;

  const newMembersBannerText = useMemo(
    () =>
      `You have ${currentGroup.get('newMemberPnmsCount', 0)} new 
    ${pluralize('member', currentGroup.get('newMemberPnmsCount', 0))} 
    to report to ${reportingPlatformDisplayName}.`,
    [currentGroup, reportingPlatformDisplayName]
  );

  const initializeListType = () => {
    if (isCouncilLeadPage) return 'table';
    if (!isFreeSubscription) {
      const stored = localStorage.getItem('leadListViewType');
      if (stored === 'card' || stored === 'table') return stored;
    }
    return 'card';
  };

  const [listType, setListType] = useState<'card' | 'table'>(
    initializeListType
  );
  const [allSelected, setAllSelected] = useState(false);
  const [actionLeadIds, setActionLeadIds] = useState<number[]>([]);
  const handleResetSelected = useCallback(() => {
    setActionLeadIds([]);
    setAllSelected(false);
  }, []);

  useEffect(() => {
    if (!isFreeSubscription) {
      localStorage.setItem('leadListViewType', listType);
    }
  }, [isFreeSubscription, listType]);

  const handleSelectAll = useCallback(() => {
    setActionLeadIds([]);
    setAllSelected(true);
  }, []);
  const handleSelectAllFetched = useCallback(
    () => setActionLeadIds(leads.map(lead => lead.id)),
    [leads]
  );
  const selectLead = useCallback(
    leadId =>
      setActionLeadIds(actionLeadIds =>
        actionLeadIds.includes(leadId)
          ? actionLeadIds.filter(actionLeadId => actionLeadId !== leadId)
          : [...actionLeadIds, leadId]
      ),
    []
  );

  const navigateToReportingPage = useCallback(() => {
    navigate(`/review-members${search ? '?' + search : ''}`);
  }, [navigate, search]);

  const navigateToDuplicatesPage = useCallback(() => {
    navigate('/leads/duplicates');
  }, [navigate]);

  const canMergeDuplicates = useSelector(
    getPermission('pnmDuplicate.deleteMany')
  );

  const handleListTypeToggle = () => {
    const newListType = listType === 'card' ? 'table' : 'card';
    navigate(`${pathname}${search ? '?' + search : ''}`, {
      state: { ...state, listType: newListType },
    });
    setListType(newListType);
  };
  const handleFetchNextPage = useCallback(() => {
    if (!loading) fetchNextPage();
  }, [loading, fetchNextPage]);

  useEffect(() => {
    if (isCouncilLeadPage && listType === 'table') {
      dispatch(fetchStatusesAction({ groupId: currentGroupId }));
      dispatch(fetchTagsAction({ groupId: currentGroupId }));
    }
  }, []); // eslint-disable-line

  return (
    <Box height='100%' id='leadListContainer' clone>
      <Grid container direction='column' wrap='nowrap'>
        <Grid item>
          <LeadListActions
            onListTypeToggle={handleListTypeToggle}
            actionLeadIds={actionLeadIds}
            onSelectAll={handleSelectAll}
            leads={leads}
            totalLeadsCount={totalLeadsCount}
            allSelected={allSelected}
            onSelectAllFetched={handleSelectAllFetched}
            onResetSelected={handleResetSelected}
            listType={listType}
            isCouncilLeadPage={isCouncilLeadPage}
            tags={tags}
            leadsQuery={{
              queryKey: (data as LeadsQueryData)?.queryKey,
              refetchLeads,
            }}
            pushLead={pushLead}
            groupId={currentGroupId}
          />
        </Grid>
        <Box paddingY={2} paddingX={3}>
          <Grid container spacing={3}>
            {reportingPlatformDisplayName &&
              Boolean(currentGroup.get('newMemberPnmsCount', 0)) && (
                <Grid item xs={12} md={6}>
                  <Banner
                    buttonLabel='Review Members'
                    header='Congratulations!'
                    subtitle={newMembersBannerText}
                    onButtonClick={navigateToReportingPage}
                  />
                </Grid>
              )}
            {canMergeDuplicates && currentGroup.get('hasDuplicatePnms', false) && (
              <Grid item xs={12} md={6}>
                <Banner
                  buttonLabel='Manage Duplicates'
                  header='Let’s clean those up!'
                  subtitle='We’ve found duplicate leads on your list.'
                  icon={<NewReleases />}
                  onButtonClick={navigateToDuplicatesPage}
                />
              </Grid>
            )}
          </Grid>
        </Box>
        <Box clone overflow='hidden'>
          <Grid item xs>
            {listType === 'card' && (
              <LeadListCardView
                allSelected={allSelected}
                hasNextPage={hasNextPage}
                leads={leads}
                loading={loading}
                nextPage={nextPage}
                onFetchNextPage={handleFetchNextPage}
                actionLeadIds={actionLeadIds}
                selectLead={selectLead}
                withPaddingTop={
                  !Boolean(
                    currentGroup.get('hasDuplicatePnms', false) ||
                      currentGroup.get('newMemberPnmsCount', 0)
                  )
                }
              />
            )}
            {listType === 'table' && (
              <LeadListTableView
                leads={leads}
                onFetchNextPage={handleFetchNextPage}
                hasNextPage={hasNextPage}
                allSelected={allSelected}
                actionLeadIds={actionLeadIds}
                selectLead={selectLead}
                loading={loading}
                withMarginTop={Boolean(
                  currentGroup.get('hasDuplicatePnms', false) ||
                    currentGroup.get('newMemberPnmsCount', 0)
                )}
                isCouncilLeadPage={isCouncilLeadPage}
                tags={tags}
                leadsQuery={{
                  queryKey: (data as LeadsQueryData)?.queryKey,
                  refetchLeads,
                }}
              />
            )}
          </Grid>
        </Box>
      </Grid>
    </Box>
  );
}
